<script setup>
import { usePlaylistStore } from "@/stores/playlist.js";
import { enableScroll, disableScroll, scrollToSlide } from "@/Helpers/scroll";
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import SingleActionModal from '@/Pages/Playlist/components/Misc/SingleActionModal.vue';

const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    cardIndex: {
        type: Number,
        default: undefined,
    },
    playlistId: {
        type: Number,
        default: undefined,
    },
    isIntersecting: Boolean,
    brand: {
        type: Object,
        default: undefined,
    },
    scormExport: Boolean,
})

const store = usePlaylistStore();

const themeContent = computed(() => store.cards?.[props.cardIndex]?.theme?.content ?? {});

const headingFont = computed(() => {
    const font = themeContent.value.heading_font || themeContent.value.font;
    return font ? font.replace(/_/g, ' ') : '';
});

const bodyFont = computed(() => {
    const font = themeContent.value.font;
    return font ? font.replace(/_/g, ' ') : '';
});
const instance = getCurrentInstance();
let modalOpen = ref(false);
let modalTitle = ref('');
let modalBody = ref('');
const isAnswered = ref(false);

const scormApi = ref(null);
if (props.scormExport) {
    scormApi.value = instance.appContext.config.globalProperties.$scorm.api;
}

function openModal() {
    modalOpen.value = true;
}

const componentID = 'a'+Math.random().toString(36).slice(2, 7);
const answerMessage = ref('');
const choices = ref(props.row.content.answers.map(answer => answer.content));

watch(
    () => props.isIntersecting,
    (newVal, oldVal) => { 
        if (props.isIntersecting === true && isAnswered.value === false) {
            disableScroll();
        } else if (props.isIntersecting === true && isAnswered.value === true) {
            enableScroll();
        }
        if (newVal === false && oldVal === true) {
            enableScroll();
        }
    }
);

watch(
    () => modalOpen.value,
    (newVal, oldVal) => {
        if (newVal === false && oldVal) {
            if (props.row.content.autoSkip === 'true') {
                scrollToSlide(store.currentSlideIndex + 1);
            }
        }
    }
);

function setPreviousAnswer() {
    const answer = store.getPreviousAnswer(props.row.id);
    if (answer) {
        let answers = answer.split(',');
        answers.forEach((item) => {
            const elem = document.querySelector('input[value="'+item+'"]');
            if (elem) elem.click();
        });
        checkQuizAnswer(null, true);
    }
}

onMounted(() => {
    setPreviousAnswer()
});

function checkQuizAnswer(event, isPrev = false) {
    if (props.row.content.multiAnswer === 'true') {
        const answerItems = document.querySelectorAll(`#${componentID} input[name="multiAnswer-${componentID}"]:checked`);
        const allAnswers = document.querySelectorAll(`#${componentID} input[name="multiAnswer-${componentID}"]`);
        if (answerItems.length > 0) {
            allAnswers.forEach((answer) => {
                answer.setAttribute('disabled', 'disabled');
            });
            const selectedAnswers = Array.from(answerItems).map(item => item.value);
            
            // Check if all selected answers are correct and if all correct answers are selected
            const correctAnswers = props.row.content.answers.filter(answer => answer.correct).map(answer => answer.content);
            const isCorrect = correctAnswers.length === selectedAnswers.length &&
                correctAnswers.every(answer => selectedAnswers.includes(answer));
            if (isCorrect) {
                modalTitle.value = "Correct"
                modalBody.value = props.row.content.correctMessage;
            } else {
                modalTitle.value = "Incorrect"
                modalBody.value = props.row.content.incorrectMessage;
            }
            if (!isPrev) openModal();
            if (!isPrev) saveResponse(props.row.content.question, selectedAnswers, isCorrect);
            enableScroll();
            isAnswered.value = true;
        } else {
            alert('You must select one of the answers');
        }
    } else {
        let answerItem = document.querySelector(`#${componentID} input[name="singleAnswer-${componentID}"]:checked`);
        const allAnswers = document.querySelectorAll(`#${componentID} input[name="singleAnswer-${componentID}"]`);
        if (answerItem) {
            allAnswers.forEach((answer) => {
                answer.setAttribute('disabled', 'disabled');
            });
            const selectedAnswerIndex = parseInt(answerItem.getAttribute('id').replace('radio-'+componentID+'-',''));
            const isCorrect = props.row.content.answers[selectedAnswerIndex].correct;
            if (isCorrect) {
                modalTitle.value = "Correct"
                modalBody.value = props.row.content.correctMessage;
            } else {
                modalTitle.value = "Incorrect"
                modalBody.value = props.row.content.incorrectMessage;
            }
            if (!isPrev) openModal();
            if (!isPrev) saveResponse(props.row.content.question, answerItem.value, isCorrect);
            enableScroll();
            isAnswered.value = true;
        } else {
            alert('You must select one of the answers');
        }
    }
}

function saveResponse(strQuestion, strAnswer, isCorrect) {
    if (typeof strAnswer === 'object') strAnswer = strAnswer.toString();

    const data = {
        playlist_id: props.playlistId,
        playlist_card_row_id: props.row.id,
        type: props.row.type,//'Confidence', 'ConfidenceConfirmation'
        question: strQuestion,
        answer: strAnswer,
        isCorrect: isCorrect,
    }

    if (props.scormExport && scormApi.value) {
        data.choices = choices.value;
        data.correct_responses = props.row.content.answers.filter(answer => answer.correct).map((answer, index) => index);
        scormApi.value.recordInteraction(data);
    } else {
        const url = '/activity/store_response'
        axios.post(url, data)
            .then(function (response) {
                // do nothing
            })
            .catch(function (error) {
                // do nothing
            });
    }
}
</script>
<template>
    <div :id="componentID" class="relative p-fl-4 w-full">
        <div class="font-black text-fl-lg w-full pb-fl-2" v-text="row.content.question" />
        <ul class="flex flex-col gap-y-1.5 pb-fl-2 my-2">
            <li
                v-for="(answer, index) in row.content.answers"
                :key="`checkbox-${index}`"
                class="flex flex-row gap-1.5 items-center w-full p-2 rounded-lg border"
            >
                <template v-if="row.content.multiAnswer === 'true'">
                    <input :id="`checkbox-${componentID}-${index}`" type="checkbox" :value="answer.content" :name="`multiAnswer-${componentID}`">
                    <label class="grow" :for="`checkbox-${componentID}-${index}`">{{ answer.content }}</label>
                </template>
                <template v-else>
                    <input :id="`radio-${componentID}-${index}`" type="radio" :value="answer.content" :name="`singleAnswer-${componentID}`">
                    <label class="grow" :for="`radio-${componentID}-${index}`">{{ answer.content }}</label>
                </template>                
            </li>
        </ul>
        <div v-if="modalBody == ''" class="flex flex-col w-full">
            <button
                type="button"
                class="place-self-end rounded-full 
                px-fl-4 
                py-fl-2 
                font-bold 
                shadow-sm 
                border-2
                focus-visible:outline 
                focus-visible:outline-2 
                focus-visible:outline-offset-2 
                focus-visible:outline-white"
                @click="checkQuizAnswer"
            >
                Submit
            </button>
        </div>
        <SingleActionModal
            v-model="modalOpen"
            :modal-title="modalTitle"
            :modal-body="modalBody"
            :brand="brand"
            :heading-font="headingFont"
            :body-font="bodyFont"
        />
    </div>
</template>

<style scoped>
button, li {
    border-color: var(--button_bg);
    background-color: var(--button_bg);
    color: var(--button_text);
}

button:hover, li:hover {
    background-color: var(--button_hover_bg);
    color: var(--button_hover_text);
}
</style>